import 'ag-grid-enterprise';

import { Box, Stack } from '@mui/material';
import type {
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
  RowClassParams
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import LoadingIndicator from '@/components/LoadingIndicator';
import useGridTheme from '@/hooks/useGridTheme';
import useDeviceQueryParam, {
  useDeviceSelectionParams
} from '@/pages/Administration/Devices/Devices.hooks';
import { selectedOrganizationIdAtom } from '@/pages/Organizations';
import { reconcileGridSelection } from '@/utils/Grid.utils';

import { filteredDevicesAtom } from '../../pages/Administration/Devices/Devices.store';
import {
  autoGroupColumnDef,
  createDeviceColumnDefs
} from './DevicesTable.constant';
import { useDeviceListing } from './DevicesTable.hooks';
import useStyles from './DevicesTable.styles';
import type { DevicesTableProps, DeviceTreeNode } from './DevicesTable.types';
import { getSharedTreeGridOptions } from './DevicesTable.utils';
import DevicesTableHeader from './DevicesTableHeader/DevicesTableHeader';
import { useDeviceAutoRefresh, useDevicesRowSelection } from './hooks';
import { buildDeviceTreeRows } from './utils/treeBuilder.utils';

/**
 * DevicesTable Component
 *
 * Main devices table component using AG Grid Tree Data for hierarchical display.
 * Displays:
 * - Device nodes (level 0) with device info
 * - Source nodes (level 1) for LIMA aggregated sources and flat device sources
 * - Provider nodes (level 2) for Microsoft child providers
 *
 * Features:
 * - Tree structure with expand/collapse
 * - Row selection and highlighting
 * - Action menus for devices and sources
 * - Auto-refresh every 5 minutes
 * - Pagination
 * - Filtering via search bar
 *
 * @param props - Component props
 * @returns JSX element with AG Grid table
 */
const DevicesTable = ({
  closeDeviceDetail,
  detailPanelVisible,
  isMainDevicePage
}: DevicesTableProps) => {
  const { classes } = useStyles();
  const gridRef = useRef<AgGridReact<DeviceTreeNode>>(null);
  const gridTheme = useGridTheme();
  const { deviceUid, sourceUid } = useDeviceSelectionParams();
  const { page } = useDeviceQueryParam();

  // Atoms
  const selectedOrganizationId = useAtomValue(
    selectedOrganizationIdAtom
  ) as number;
  const [filteredDevices] = useAtom(filteredDevicesAtom);
  const selectedDetailRowId = sourceUid || deviceUid;

  // API queries and data processing
  const { devicesQuery, isSyncInProgressQuery } = useDeviceListing();

  // Use extracted hooks for focused concerns
  const selection = useDevicesRowSelection();

  // Auto-refresh hook with 5-minute interval
  useDeviceAutoRefresh(devicesQuery, isSyncInProgressQuery, closeDeviceDetail);

  // ============================================================================
  // GRID CONFIGURATION
  // Transform filtered devices to tree data and derive row-id lookup set.
  // ============================================================================
  const { treeData, filteredTreeRowIds } = useMemo(() => {
    const rows = buildDeviceTreeRows(filteredDevices, {
      selectedOrganizationId,
      includeMicrosoftProviders: true
    }).filter((row) => {
      // Hiding logforwarder's log sources
      if (row?.type === 'source' && row?.sourceData?.source_type_id === 24) {
        return false;
      }
      return true;
    });

    return {
      treeData: rows,
      filteredTreeRowIds: new Set(rows.map((row) => row.id))
    };
  }, [filteredDevices, selectedOrganizationId]);

  // Column definitions
  const columnDefs = useMemo(() => {
    return createDeviceColumnDefs(selectedOrganizationId, isMainDevicePage);
  }, [selectedOrganizationId, isMainDevicePage]);

  // Grid options - using built-in AG Grid features
  const gridOptions = useMemo(() => {
    return getSharedTreeGridOptions<DeviceTreeNode>({
      autoGroupField: 'name',
      autoGroupHeaderName: 'Device Name',
      innerRenderer: autoGroupColumnDef.cellRendererParams?.innerRenderer,
      suppressExpandIcon:
        autoGroupColumnDef.cellRendererParams?.suppressExpandIcon
    });
  }, []);

  // Stable getRowClass - reads selectedDeviceUidRef so it always has the
  // current URL value at the moment AG Grid calls it during redrawRows().
  const getRowClass = useCallback(
    (params: RowClassParams<DeviceTreeNode>): string[] => {
      const classes: string[] = [];
      if (params.data?.level === 1) classes.push('device-main-table-level-1');
      if (params.data?.level === 2) classes.push('device-main-table-level-2');
      if (params.node.lastChild) classes.push('device-main-table-row-is-last');
      if (params.node.isSelected()) {
        classes.push('ag-grid-device-main-table-row-selected');
      }
      return classes;
    },
    []
  );

  const reconcileSelectionFromUrl = useCallback(
    (api: GridApi<DeviceTreeNode>) => {
      reconcileGridSelection({
        api,
        selectedRowId: selectedDetailRowId,
        filteredRowIds: filteredTreeRowIds,
        onMissingSelection: closeDeviceDetail
      });
    },
    [closeDeviceDetail, filteredTreeRowIds, selectedDetailRowId]
  );

  // Reset pagination when organization changes
  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.paginationGoToFirstPage();
    }
  }, [selectedOrganizationId]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    reconcileSelectionFromUrl(api);
  }, [reconcileSelectionFromUrl]);

  const onFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent<DeviceTreeNode>) => {
      reconcileSelectionFromUrl(event.api);
    },
    [reconcileSelectionFromUrl]
  );

  const onFilterChanged = useCallback(
    (event: FilterChangedEvent<DeviceTreeNode>) => {
      reconcileSelectionFromUrl(event.api);
    },
    [reconcileSelectionFromUrl]
  );

  const onGridReady = useCallback((event: GridReadyEvent<DeviceTreeNode>) => {
    event.api.applyColumnState({
      state: [
        {
          colId: 'lastSeen',
          sort: 'desc'
        }
      ],
      defaultState: {
        sort: null
      }
    });
  }, []);

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <Stack direction="column" className={classes.requestsTableWrapper}>
      <Box className={classes.searchBar}>
        <DevicesTableHeader collapsed={!detailPanelVisible} />
      </Box>
      <LoadingIndicator isLoading={devicesQuery.isLoading} />
      <Box sx={{ height: '100%', width: '100%' }}>
        <AgGridReact<DeviceTreeNode>
          ref={gridRef}
          theme={gridTheme}
          rowData={treeData}
          columnDefs={columnDefs}
          rowHeight={70}
          treeData={true}
          animateRows={true}
          groupDefaultExpanded={0}
          getDataPath={(data: DeviceTreeNode) => data.path}
          autoGroupColumnDef={autoGroupColumnDef}
          gridOptions={gridOptions}
          pagination={true}
          paginationPageSize={25}
          paginationPageSizeSelector={[25, 50, 100]}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered}
          onFilterChanged={onFilterChanged}
          onCellClicked={selection.onCellClicked}
          overlayNoRowsTemplate="<span>No matches found</span>"
          getRowClass={getRowClass}
          context={{ page }}
          suppressRowClickSelection={true}
          suppressCellFocus={true}
          suppressContextMenu={true}
        />
      </Box>
    </Stack>
  );
};

export default DevicesTable;
